<script setup lang="ts">
import { AppWindow, ArrowRight, SearchX, Zap } from "@lucide/vue";
import { computed } from "vue";
import { useMyTools } from "@/api/me";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import AccessBadge from "@/components/tools/AccessBadge.vue";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";
import { firstSentence, groupTools, toolAccess, toolTargets } from "@/lib/tools";

definePage({ meta: { title: "Tools", layout: "wide" } });

const tools = useMyTools();
const q = useRouteQuery("q");
const domain = useRouteQuery("domain");
const access = useRouteQuery("access");

const allGroups = computed(() => groupTools(tools.data.value ?? []));
const filtered = computed(() => {
  const needle = q.value.trim().toLowerCase();
  return groupTools(
    (tools.data.value ?? []).filter((tool) => {
      if (access.value && toolAccess(tool) !== access.value) return false;
      if (!needle) return true;
      return [tool.name, tool.title, tool.description].some((s) => s?.toLowerCase().includes(needle));
    }),
  ).filter((g) => !domain.value || g.key === domain.value);
});

const domainOptions = computed(() => allGroups.value.map((g) => ({ value: g.key, label: g.domain.label, count: g.tools.length })));
const accessOptions = [
  { value: "read", label: "Only reads" },
  { value: "write", label: "Can change" },
];
const domainModel = computed({ get: () => domain.value || undefined, set: (v: string | string[] | undefined) => (domain.value = (v as string) ?? "") });
const accessModel = computed({ get: () => access.value || undefined, set: (v: string | string[] | undefined) => (access.value = (v as string) ?? "") });
</script>

<template>
  <div>
    <PageHeader :title="t.tools.title" :description="t.tools.description">
      <template #actions>
        <Button as-child variant="outline"><RouterLink to="/actions"><Zap /> {{ t.tools.runbooks }}</RouterLink></Button>
        <Button as-child variant="outline"><RouterLink to="/apps"><AppWindow /> {{ t.apps.link }}</RouterLink></Button>
      </template>
    </PageHeader>

    <FilterBar v-model:search="q" :placeholder="t.tools.searchPlaceholder" label="Filter tools" class="mb-5">
      <template #chips>
        <FilterChips v-if="domainOptions.length > 1" v-model="domainModel" :options="domainOptions" label="Area" />
        <FilterChips v-model="accessModel" :options="accessOptions" label="Access" />
      </template>
    </FilterBar>

    <div v-if="tools.isPending.value" class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      <Skeleton v-for="i in 8" :key="i" class="h-32 rounded-xl" />
    </div>
    <ErrorState v-else-if="tools.error.value" :error="tools.error.value" @retry="tools.refetch()" />
    <EmptyState v-else-if="filtered.length === 0" :icon="SearchX" :title="t.tools.empty" description="Try another word, or clear the filters.">
      <Button variant="outline" @click="q = ''; domain = ''; access = ''">{{ t.common.clear }}</Button>
    </EmptyState>
    <div v-else class="flex flex-col gap-8">
      <section v-for="g in filtered" :key="g.key" :aria-labelledby="`grp-${g.key}`">
        <header class="mb-3 flex items-center gap-3">
          <span class="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20"><component :is="g.domain.icon" class="size-[18px]" aria-hidden="true" /></span>
          <div class="min-w-0">
            <h2 :id="`grp-${g.key}`" class="section-title flex items-baseline gap-2">
              {{ g.domain.label }} <span class="text-xs font-normal text-muted-foreground tabular-nums">{{ t.home.tools(g.tools.length) }}</span>
            </h2>
            <p v-if="g.domain.description" class="text-[13px] text-muted-foreground">{{ g.domain.description }}</p>
          </div>
        </header>
        <ul class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          <li v-for="tool in g.tools" :key="tool.name">
            <Card variant="interactive" padding="sm" as-child class="group h-full">
              <RouterLink :to="`/tools/${tool.name}`">
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p class="card-title leading-snug">{{ tool.title ?? tool.name }}</p>
                    <p class="truncate font-mono text-xs text-muted-foreground">{{ tool.name }}</p>
                  </div>
                  <AccessBadge :access="toolAccess(tool)" />
                </div>
                <p class="line-clamp-3 text-sm text-muted-foreground">{{ firstSentence(tool.description, 180) }}</p>
                <p class="mt-auto flex items-center gap-1 pt-1 text-xs font-medium text-muted-foreground group-hover:text-primary">
                  <template v-if="toolTargets(tool).length"><span class="font-normal text-muted-foreground">{{ t.tools.targetCount(toolTargets(tool).length) }} ·</span></template>
                  {{ t.tools.tryIt }} <ArrowRight class="size-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </p>
              </RouterLink>
            </Card>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
